import { get, post, put, del } from '../utils/request';

// Follow-up target type enum
export enum FollowUpTargetType {
  LEAD = 'lead',
  CASE = 'case',
}

// Follow-up interface
export interface FollowUp {
  id: number;
  entity_type?: string; // New field for compatibility
  entity_id?: number; // New field for compatibility
  target_type?: FollowUpTargetType;
  target_id?: number;
  follow_up_type?: string; // New field name
  follow_type?: string;
  content?: string;
  phone_number?: string;
  responsible_person_id?: number;
  attachments?: any[];
  next_follow_up_time?: string;
  reminder_enabled?: boolean;
  created_by: number;
  created_by_name?: string; // New field name
  created_at: string;
  updated_at?: string;
  creator_name?: string;
  responsible_person_name?: string;
  target_name?: string;
  target_number?: string;
}

// Follow-up list response interface
export interface FollowUpListResponse {
  items: FollowUp[];
  total: number;
  page: number;
  page_size: number;
  pages: number;
}

// Create follow-up request interface
export interface CreateFollowUpRequest {
  entity_type?: string; // New field for compatibility
  entity_id?: number; // New field for compatibility
  target_type?: FollowUpTargetType;
  target_id?: number;
  follow_type?: string;
  content?: string;
  phone_number?: string;
  responsible_person_id?: number;
  attachments?: any[];
  next_follow_up_time?: string;
  reminder_enabled?: boolean;
}

// Update follow-up request interface
export interface UpdateFollowUpRequest {
  follow_type?: string;
  content?: string;
  phone_number?: string;
  responsible_person_id?: number;
  attachments?: any[];
  next_follow_up_time?: string;
  reminder_enabled?: boolean;
}

// Follow-up list request interface
export interface FollowUpListRequest {
  entity_type?: string; // New field for compatibility
  entity_id?: number; // New field for compatibility
  target_type?: FollowUpTargetType; // Backend field
  target_id?: number; // Backend field
  page?: number;
  pageSize?: number; // Alternative naming
  page_size?: number;
  keyword?: string;
  follow_type?: string;
  created_by?: number;
  responsible_person_id?: number;
  start_date?: string;
  end_date?: string;
}

// Follow-up statistics interface
export interface FollowUpStatistics {
  total_follow_ups: number;
  lead_follow_ups: number;
  case_follow_ups: number;
  my_follow_ups: number;
  recent_follow_ups: number;
  follow_type_breakdown: Record<string, number>;
  current_month_follow_ups: number;
  last_month_follow_ups: number;
}

// Follow-up target info interface
export interface FollowUpTargetInfo {
  target_type: FollowUpTargetType;
  target_id: number;
  target_name: string;
  target_number: string;
  follow_up_count: number;
  last_follow_up_date?: string;
}

// Batch create follow-ups request interface
export interface BatchCreateFollowUpRequest {
  targets: Array<{
    target_type: FollowUpTargetType;
    target_id: number;
  }>;
  follow_type: string;
  content: string;
  phone_number?: string;
  responsible_person_id?: number;
}

/**
 * Create new follow-up
 */
export const createFollowUp = async (data: CreateFollowUpRequest): Promise<FollowUp> => {
  // Map entity_type/entity_id to target_type/target_id for backend compatibility
  const requestData = {
    target_type: data.entity_type || data.target_type,
    target_id: data.entity_id || data.target_id,
    follow_type: data.follow_type,
    content: data.content,
    phone_number: data.phone_number,
    responsible_person_id: data.responsible_person_id,
    attachments: data.attachments,
    next_follow_up_time: data.next_follow_up_time,
    reminder_enabled: data.reminder_enabled,
  };
  return post<FollowUp>('/follow-ups', requestData);
};

/**
 * Batch create follow-ups
 */
export const batchCreateFollowUps = async (data: BatchCreateFollowUpRequest): Promise<FollowUp[]> => {
  return post<FollowUp[]>('/follow-ups/batch', data);
};

/**
 * Get follow-ups with pagination and filters
 */
export const getFollowUps = async (params: FollowUpListRequest): Promise<FollowUpListResponse> => {
  // Map entity_type/entity_id to target_type/target_id for backend compatibility
  const requestParams = {
    ...params,
    target_type: params.entity_type || params.target_type,
    target_id: params.entity_id || params.target_id,
    page_size: params.pageSize || params.page_size,
  };
  // Remove entity_type and entity_id from params
  delete requestParams.entity_type;
  delete requestParams.entity_id;
  delete requestParams.pageSize;
  
  return get<FollowUpListResponse>('/follow-ups', { params: requestParams });
};

/**
 * Get my follow-ups (created by me or assigned to me)
 */
export const getMyFollowUps = async (params: {
  page?: number;
  page_size?: number;
  as_creator?: boolean;
}): Promise<FollowUpListResponse> => {
  return get<FollowUpListResponse>('/follow-ups/my-follow-ups', { params });
};

/**
 * Get recent follow-ups within specified days
 */
export const getRecentFollowUps = async (params: {
  days?: number;
  page?: number;
  page_size?: number;
}): Promise<FollowUpListResponse> => {
  return get<FollowUpListResponse>('/follow-ups/recent', { params });
};

/**
 * Get follow-up targets with counts
 */
export const getFollowUpTargets = async (params: {
  target_type?: FollowUpTargetType;
  page?: number;
  page_size?: number;
}): Promise<FollowUpTargetInfo[]> => {
  return get<FollowUpTargetInfo[]>('/follow-ups/targets', { params });
};

/**
 * Get follow-up statistics
 */
export const getFollowUpStatistics = async (): Promise<FollowUpStatistics> => {
  return get<FollowUpStatistics>('/follow-ups/statistics');
};

/**
 * Get upcoming follow-up reminders
 */
export const getUpcomingReminders = async (params: {
  target_type?: FollowUpTargetType;
  target_id?: number;
  days_ahead?: number;
  page?: number;
  page_size?: number;
}): Promise<FollowUp[]> => {
  return get<FollowUp[]>('/follow-ups/reminders/upcoming', { params });
};

/**
 * Get follow-ups for a specific target
 */
export const getTargetFollowUps = async (
  targetType: FollowUpTargetType,
  targetId: number,
  params: {
    page?: number;
    page_size?: number;
  }
): Promise<FollowUp[]> => {
  return get<FollowUp[]>(`/follow-ups/target/${targetType}/${targetId}`, { params });
};

/**
 * Get follow-up by ID
 */
export const getFollowUpById = async (id: number): Promise<FollowUp> => {
  return get<FollowUp>(`/follow-ups/${id}`);
};

/**
 * Update follow-up
 */
export const updateFollowUp = async (id: number, data: UpdateFollowUpRequest): Promise<FollowUp> => {
  return put<FollowUp>(`/follow-ups/${id}`, data);
};

/**
 * Delete follow-up
 */
export const deleteFollowUp = async (id: number): Promise<void> => {
  return del<void>(`/follow-ups/${id}`);
};